import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileSearch, ShieldCheck, XCircle } from "lucide-react";

export default function AdminKycQueue() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/kyc/pending");
      setPending(data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const decide = async (status) => {
    if (!selected) return;
    setActing(true);
    try {
      await api.post(`/admin/kyc/${selected.id}/decision`, { status, remarks });
      toast.success(`KYC ${status}`);
      setSelected(null);
      setRemarks("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Decision failed");
    } finally {
      setActing(false);
    }
  };

  return (
    <DashboardLayout>
      <div>
        <span className="overline">Admin</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
          KYC review queue
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl">
          Approve or reject partner KYC submissions. Verified partners can start processing BBPS
          &amp; payout transactions once Phase 2 ships.
        </p>

        <div className="mt-8 surface-card overflow-hidden">
          <Table data-testid="kyc-queue-table">
            <TableHeader>
              <TableRow>
                <TableHead>Partner</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>PAN</TableHead>
                <TableHead>Business</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : pending.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                    <div className="flex flex-col items-center gap-2">
                      <FileSearch className="h-6 w-6" strokeWidth={1.5} />
                      No pending KYC submissions.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                pending.map((p) => (
                  <TableRow key={p.id} data-testid={`kyc-row-${p.id}`}>
                    <TableCell>
                      <div className="font-medium">{p.full_name}</div>
                      <div className="text-xs text-muted-foreground">{p.email}</div>
                    </TableCell>
                    <TableCell className="font-mono">{p.mobile}</TableCell>
                    <TableCell className="font-mono">{p.kyc?.pan_number || "-"}</TableCell>
                    <TableCell>
                      <div>{p.kyc?.business_name || p.business_name || "-"}</div>
                      <div className="text-xs text-muted-foreground">{p.kyc?.business_type}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {p.kyc?.submitted_at?.slice(0, 10)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelected(p)}
                        data-testid={`review-${p.id}`}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review KYC — {selected?.full_name}</DialogTitle>
            <DialogDescription>
              Verify documents and business details before approving.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Field label="Email" value={selected.email} />
                <Field label="Mobile" value={selected.mobile} mono />
                <Field label="Business" value={selected.kyc?.business_name} />
                <Field label="Type" value={selected.kyc?.business_type} />
                <Field label="PAN" value={selected.kyc?.pan_number} mono />
                <Field label="Aadhaar" value={selected.kyc?.aadhaar_number} mono />
                <Field label="GST" value={selected.kyc?.gst_number || "—"} mono />
                <Field
                  label="Address"
                  value={`${selected.kyc?.address_line1 || ""}, ${selected.kyc?.city || ""}, ${selected.kyc?.state || ""} ${selected.kyc?.pincode || ""}`}
                />
              </div>
              {selected.kyc?.documents?.length > 0 && (
                <div>
                  <span className="overline">Documents</span>
                  <ul className="mt-2 space-y-1 text-sm">
                    {selected.kyc.documents.map((d, i) => (
                      <li key={`${d.name}-${i}`}>
                        <a
                          href={d.data_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline"
                          data-testid={`doc-link-${i}`}
                        >
                          {d.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <Label htmlFor="remarks">Remarks (optional)</Label>
                <Textarea
                  id="remarks"
                  data-testid="admin-remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="mt-1"
                  placeholder="Add a note for the partner (visible on rejection)"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => decide("rejected")}
              disabled={acting}
              data-testid="btn-reject"
              className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <XCircle className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Reject
            </Button>
            <Button onClick={() => decide("verified")} disabled={acting} data-testid="btn-approve">
              <ShieldCheck className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const Field = ({ label, value, mono }) => (
  <div>
    <div className="overline">{label}</div>
    <div className={`mt-1 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
  </div>
);
